import { forwardRef, type ReactNode, useCallback } from "react";
import {
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  type PopoverOrigin,
  Select as MUISelect,
  type SelectProps as MUISelectProps,
} from "@mui/material";
import { FontSize, getFontStyle } from "../materialInput/Fonts";
import {
  C_ERROR,
  C_INFORMATION,
  C_ON_SURFACE,
  C_ON_SURFACE_VARIANT,
  C_OUTLINE_VARIANT,
  C_PRIMARY,
  C_PRIMARY_HOVER,
  C_SECONDARY_DISABLED,
} from "../icon/Colors";
import { makeStyles, withStyles } from "@mui/styles";
import classNames from "classnames";
import { useBooleanState } from "../../hooks/useBooleanState";
import { INPUT_FONT } from "../materialInput/MaterialInput";

export type SelectProps = MUISelectProps & {
  helperLabel?: ReactNode;
  label?: ReactNode;
  disabled?: boolean;
  "data-test-id"?: string;
  error?: boolean;
  fullWidth?: boolean;
  highlight?: boolean;
  shrink?: boolean;
  hideHelper?: boolean;
};

const useStyles = makeStyles({
  select: {
    "&:focus": {
      backgroundColor: "transparent",
    },
  },
  cssFocused: {
    color: C_PRIMARY,
  },
  cssLabel: {
    fontWeight: "normal",
    textTransform: "none",
    ...INPUT_FONT,
    color: C_ON_SURFACE,
    "&$cssFocused": {
      color: C_PRIMARY,
    },
  },
  cssLabelTextArea: {
    top: "10px",
    left: "10px",
    "*:hover > &:not(.Mui-disabled)": {
      color: C_PRIMARY,
    },
  },
  helperLabel: {
    ...getFontStyle(FontSize.LabelLarge),
    minHeight: getFontStyle(FontSize.LabelLarge).lineHeight,
    marginTop: "4px",
  },
  helperLabelColor: {
    color: `${C_ON_SURFACE_VARIANT} !important`,
  },
  greenHelperLabelColor: {
    color: `${C_PRIMARY} !important`,
  },
  cssShrink: {
    // 16px * 0.75 scaling = 12px
    ...getFontStyle(FontSize.BodyLarge),
    color: C_ON_SURFACE_VARIANT,
  },
  cssError: {
    color: `${C_ERROR} !important`,
  },
  cssInfo: {
    color: `${C_INFORMATION} !important`,
  },
  cssInfoUnderline: {
    "&:before": {
      borderBottomColor: `${C_INFORMATION} !important`,
    },
  },
  cssUnderlineError: {
    "&:before": {
      borderBottomColor: `${C_ERROR} !important`,
    },
  },
  cssPlaceholder: {
    "&::placeholder": {
      color: C_ON_SURFACE_VARIANT,
      opacity: 1,
    },
  },
  cssDisabled: {
    color: C_SECONDARY_DISABLED,
  },

  icon: {
    color: C_PRIMARY,
    fontSize: 15,
    top: "calc(50% - 7.5px)", // Icon size = 15px
    "&:hover": {
      color: C_PRIMARY_HOVER,
    },
  },

  paper: {
    boxShadow: "none",
    border: `1px solid ${C_OUTLINE_VARIANT}`,
    borderRadius: "0px",
  },
  cssHelperText: {
    fontSize: "12px !important",
  },
});

const StyledInput = withStyles((theme) => ({
  root: {
    ...INPUT_FONT,
    color: C_ON_SURFACE,
    padding: "0",
    "&:before": {
      borderBottomWidth: "1px",
      borderBottomColor: C_OUTLINE_VARIANT,
    },
    "&:after": {
      borderBottomWidth: "1px",
      borderBottomColor: C_PRIMARY,
    },
    "&:hover": {
      "&:before": {
        borderBottom: `1px solid ${C_ON_SURFACE} !important`,
      },
    },
  },
  input: {
    minHeight: INPUT_FONT.lineHeight,
    padding: "8px 0",
    paddingRight: "30px",
  },
}))(Input);

/**
 * @deprecated - use TextField instead
 */
export const Select = forwardRef((props: SelectProps, ref) => {
  const {
    label,
    variant,
    helperLabel,
    disabled,
    error,
    fullWidth,
    highlight,
    onChange,
    shrink,
    open,
    onOpen,
    onClose,
    hideHelper,
    MenuProps,
    ...rest
  } = props;

  const classes = useStyles();
  const { val, setTrue, setFalse } = useBooleanState(false);
  const { val: autoOpen, setTrue: setAutoTrue, setFalse: setAutoFalse } = useBooleanState(true);

  const finalOpen = open ?? val;
  const finalOnOpen = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: React.ChangeEvent<any>) => {
      setTrue();
      // @ts-ignore
      onOpen?.(e);
    },
    [onOpen],
  );
  const finalOnClose = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: React.ChangeEvent<any>) => {
      setFalse();
      // @ts-ignore
      onClose?.(e);
    },
    [onClose],
  );

  const isOutlined = props.variant === "outlined";
  const borderColor = highlight ? C_INFORMATION : C_ERROR;
  const MuiOutlinedInputBorder = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColor,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColor,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColor,
    },
  };

  return (
    <FormControl variant={variant} disabled={disabled} error={error} fullWidth={fullWidth}>
      <InputLabel
        classes={{
          root: classNames(classes.cssLabel, highlight && classes.cssInfo),
          focused: classes.cssFocused,
          shrink: classes.cssShrink,
          error: classes.cssError,
        }}
        shrink={shrink}
      >
        {label}
      </InputLabel>
      <MUISelect
        {...rest}
        label={label}
        variant={variant}
        open={finalOpen}
        onClose={(e) => {
          setAutoFalse();
          finalOnClose(e);
          // just delay the autoOpen after the next focus call was triggered
          window.setTimeout(setAutoTrue);
        }}
        onOpen={finalOnOpen}
        onFocus={(e) => !val && autoOpen && finalOnOpen(e)}
        onChange={onChange}
        classes={{
          root: classNames(highlight && classes.cssInfoUnderline, error && classes.cssUnderlineError),
          select: classes.select,
          disabled: classes.cssDisabled,
          icon: classes.icon,
        }}
        sx={highlight || error ? MuiOutlinedInputBorder : {}}
        displayEmpty={shrink}
        input={!isOutlined ? <StyledInput /> : undefined}
        MenuProps={{
          ...SELECT_MENU_POSITION_PARAMS,
          MenuListProps: {
            disablePadding: true,
          },
          ...MenuProps,
          classes: {
            ...MenuProps?.classes,
            // TODO - Move selectMenu class elsewhere
            paper: classNames(classes.paper, MenuProps?.classes?.paper),
          },
        }}
        ref={ref}
      />
      {!hideHelper && (
        <FormHelperText
          classes={{
            root: classNames(
              classes.helperLabel,
              highlight ? classes.cssInfo : classes.helperLabelColor,
              isOutlined && classes.cssHelperText,
            ),
            error: classNames(classes.cssError),
          }}
        >
          {helperLabel}
        </FormHelperText>
      )}
    </FormControl>
  );
});

export const SELECT_MENU_POSITION_PARAMS: { anchorOrigin: PopoverOrigin; transformOrigin: PopoverOrigin } = {
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "center",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "center",
  },
};
